import type {
  StopLoader,
  SimpleLoader,
  SimpleLoadingContextValue,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { createSimpleLoadingContextAndProvider } from "@tandemdiabetes/sugarmate-api-react-client"
import type {
  FC,
  ReactNode,
} from "react"
import {
  useCallback,
  useEffect,
  useMemo,
} from "react"
import type { RxCollection } from "rxdb"
import { useRxCollection } from "rxdb-hooks"
import type { LoadingLifecycleContextValue } from "./createLoadingLifecycleContextAndProvider"
import {
  createLoadingLifecycleContextAndProvider,
  defaultLoadingLifecycleContextValue,
} from "./createLoadingLifecycleContextAndProvider"
import { createLocalStorageGetterSetter } from "./createLocalStorageGetterSetter"
import type { UseReloadingContext } from "./createReloadingContextProvider"
import { createReloadingContextProvider } from "./createReloadingContextProvider"
import { useDbContext } from "src/contexts/DbContext"
import { usePageLifecycleContext } from "src/contexts/PageLifecycleContext"
import { useRefValue } from "src/hooks/utils"
import type {
  CollectionKey,
  LocalStorageKey,
} from "src/types"
import { PageLifecycle } from "src/types"

interface Props {
  children?: ReactNode;
}

type LoadedBefore = string | null

function serializeLoadedBefore(value: LoadedBefore): string {
  return `${value}`
}

function deserializeLoadedBefore(
  storedValue: string,
  defaultValue: LoadedBefore,
): LoadedBefore {
  if (storedValue === `${null}`) {
    return defaultValue
  }

  return storedValue
}

function defaultUseLoading() {
  return defaultLoadingLifecycleContextValue
}

type Params<T, U> = {
  collectionKey: CollectionKey;
  localStorageKey: LocalStorageKey;
  useLoader: () => [SimpleLoadingContextValue, SimpleLoader<T>, StopLoader];
  useDependencyLoadingLifecycle?: () => LoadingLifecycleContextValue;
  transform: (i: T) => U;
}

/*
 * this function returns a provider for the loading context of a specific
 * model and a cook for consuming that context
 *
 * the provider handles executing the loader returned by "useLoader" at
 * the appropriate time
 */
export function createSimpleLoadingContextProvider<T, U = T>(
  params: Params<T, U>,
): [FC<Props>, () => LoadingLifecycleContextValue, UseReloadingContext] {
  const {
    collectionKey,
    localStorageKey,
    useLoader,
    useDependencyLoadingLifecycle = defaultUseLoading,
    transform,
  } = params

  const [
    useLoadingContext,
    LoadingContextProvider,
  ] = createSimpleLoadingContextAndProvider()

  const [
    useLoadingLifecycleContext,
    LoadingLifecycleContextProvider,
  ] = createLoadingLifecycleContextAndProvider(useLoadingContext)

  const [
    getLoadedBefore,
    setLoadedBefore,
  ] = createLocalStorageGetterSetter({
    key: localStorageKey,
    serializeValue: serializeLoadedBefore,
    deserializeValue: deserializeLoadedBefore,
  })

  const [
    ReloadingContextProvider,
    useReloadingContext,
  ] = createReloadingContextProvider()

  const LoadingProvider = (props: Props) => {
    const rxCollection = useRxCollection(collectionKey) as RxCollection<U>
    const { isLoadingLeader } = useDbContext()
    const pageLifecycle = usePageLifecycleContext()
    const { loadedOrErrored: dependencyLoadedOrErrored } = useDependencyLoadingLifecycle()

    const upsertItems = useMemo(
      () => !rxCollection
        ? undefined
        : async (items: T[]) => {
          try {
            const response = await rxCollection.bulkUpsert(items.map(transform))
            return response
          } catch (e) {
            if (process.env.NODE_ENV === "development") {
              console.log(
                `failed to insert ${items.length} items into ${collectionKey}`,
              )
            }
            throw e
          }
        },
      [rxCollection],
    )

    const [
      loadingState,
      loader,
      stopLoader,
    ] = useLoader()

    const isVisible = pageLifecycle === PageLifecycle.Active || pageLifecycle === PageLifecycle.Passive

    const callLoader = useCallback(
      async () => {
        if (loader && upsertItems && isVisible && isLoadingLeader && dependencyLoadedOrErrored) {
          try {
            await loader(
              upsertItems,
              getLoadedBefore,
              setLoadedBefore,
            )
          } catch (e) {
            if (process.env.NODE_ENV === "development") {
              console.log(`loading ${collectionKey} failed`)
              console.log(e)
            }
          }
        }
      },
      [
        loader,
        upsertItems,
        isVisible,
        isLoadingLeader,
        dependencyLoadedOrErrored,
      ],
    )

    const callLoaderRef = useRefValue(callLoader)

    useEffect(
      () => {
        void callLoader()

        return () => {
          stopLoader()
        }
      },
      [
        callLoader,
        stopLoader,
      ],
    )

    const { reloadRequestedAt } = useReloadingContext()

    // execute the loader if a reload is requested
    useEffect(
      () => {
        if (reloadRequestedAt) {
          void callLoaderRef.current()
        }
      },
      [
        reloadRequestedAt,
        callLoaderRef,
      ],
    )

    return (
      <LoadingContextProvider {...loadingState}>
        <LoadingLifecycleContextProvider
          loadingDependencies={[
            loader,
            upsertItems,
            isVisible,
            isLoadingLeader,
            dependencyLoadedOrErrored,
          ]}
          loadingRequirements={[
            isVisible,
            isLoadingLeader,
          ]}
        >
          {props.children}
        </LoadingLifecycleContextProvider>
      </LoadingContextProvider>
    )
  }

  const Container = (props: Props) => (
    <ReloadingContextProvider>
      <LoadingProvider>
        {props.children}
      </LoadingProvider>
    </ReloadingContextProvider>
  )

  return [
    Container,
    useLoadingLifecycleContext,
    useReloadingContext,
  ]
}
